// What the buyer keeps of a package before the RFQ is drafted: which lines,
// which files. Pure, so the screen only draws the result.
// apps/smart-catalog/frontend/src/components/buyer-agent/rfq-center/packagePlanView.ts

import { PackagePlan, PackagePlanFile, PackagePlanLine } from '../../../services/rfqPolicyService';

export const FILE_KINDS = [
  { type: 'DRAWING', label: 'Drawings' },
  { type: 'CAD_MODEL', label: '3D models' },
  { type: 'PROCESS_SPEC', label: 'Process specs' },
  { type: 'REQUIREMENTS', label: 'Requirements' },
] as const;

const KIND_NAME: Record<string, string> = { DRAWING: 'Drawing', CAD_MODEL: '3D model', PROCESS_SPEC: 'Process spec', REQUIREMENTS: 'Requirements' };

export interface PackageSelection {
  /** Lines the buyer unticked. */
  offLines: Set<string>;
  /** File kinds switched off for every line. */
  offKinds: Set<string>;
  /** A single file set against its kind switch: true = send, false = leave out. */
  fileChoice: Map<string, boolean>;
}

export const emptySelection = (): PackageSelection => ({ offLines: new Set(), offKinds: new Set(), fileChoice: new Map() });

export const typeLabel = (type: string): string =>
  type === 'ASSEMBLY' ? 'Assembly' : type === 'RAW_MATERIAL' ? 'Raw' : 'Part';

export const fileLabel = (file: PackagePlanFile): string => file.fileName || KIND_NAME[file.documentType] || 'File';

/** A line is in when neither it nor any line above it was unticked. The first line is always in. */
export function isLineKept(plan: PackagePlan, line: PackagePlanLine, selection: PackageSelection): boolean {
  const byId = new Map(plan.lines.map((l) => [l.lineItemId, l]));
  for (let at: PackagePlanLine | undefined = line; at && at.parentLineItemId; at = byId.get(at.parentLineItemId)) {
    if (selection.offLines.has(at.lineItemId)) return false;
  }
  return true;
}

export function isFileSent(file: PackagePlanFile, selection: PackageSelection): boolean {
  return selection.fileChoice.get(file.documentId) ?? !selection.offKinds.has(file.documentType);
}

export function hasChildren(plan: PackagePlan, line: PackagePlanLine): boolean {
  return plan.lines.some((l) => l.parentLineItemId === line.lineItemId);
}

/** Hidden when a line above it is folded. */
export function isFolded(plan: PackagePlan, line: PackagePlanLine, closed: Set<string>): boolean {
  const byId = new Map(plan.lines.map((l) => [l.lineItemId, l]));
  for (let at = line.parentLineItemId ? byId.get(line.parentLineItemId) : undefined; at; at = at.parentLineItemId ? byId.get(at.parentLineItemId) : undefined) {
    if (closed.has(at.lineItemId)) return true;
  }
  return false;
}

/** A part to be made with no drawing cannot be quoted. */
export function needsDrawing(line: PackagePlanLine): boolean {
  return line.type !== 'RAW_MATERIAL' && !line.files.some((f) => f.documentType === 'DRAWING');
}

export interface PackageTotals {
  lines: number;
  files: number;
  /** What the draft request needs to leave the rest out. */
  excludeLineItemIds: string[];
  excludeDocumentIds: string[];
}

export function packageTotals(plan: PackagePlan, selection: PackageSelection): PackageTotals {
  const kept = plan.lines.filter((l) => isLineKept(plan, l, selection));
  const sent = new Set(kept.flatMap((l) => l.files.filter((f) => isFileSent(f, selection)).map((f) => f.documentId)));
  const all = new Set(plan.lines.flatMap((l) => l.files.map((f) => f.documentId)));
  return {
    lines: kept.length,
    files: sent.size,
    excludeLineItemIds: plan.lines.filter((l) => selection.offLines.has(l.lineItemId)).map((l) => l.lineItemId),
    excludeDocumentIds: [...all].filter((id) => !sent.has(id)),
  };
}
